/**
 * FE mirror of BE ``api.error_mapping`` / GraphQL ``extensions`` + REST error envelope.
 *
 * GraphQL ApplicationError extensions:
 *   { code: string, status: number, details?: object }
 *
 * REST ApplicationError JSON body:
 *   { status: "error", code: string, message: string, details?: object }
 */

import * as m from "$lib/paraglide/messages";

/** Matches BE ``ERROR_STATUS``. */
export const API_ERROR_STATUS = "error" as const;

/** Stable ApplicationError ``code`` values from BE ``common.exceptions``. */
export const ApiErrorCode = {
  ValidationError: "ValidationError",
  NotFoundError: "NotFoundError",
  AuthenticationError: "AuthenticationError",
  AuthorizationError: "AuthorizationError",
  ConflictError: "ConflictError",
  ExternalServiceError: "ExternalServiceError",
  InsufficientCredits: "InsufficientCredits",
  SubscriptionExpired: "subscription_expired",
  OptimisticLockError: "OPTIMISTIC_LOCK_ERROR",
  WorkflowLocked: "WORKFLOW_LOCKED",
  InternalError: "InternalError",
  NoUsableTemplate: "NoUsableTemplate",
} as const;

export type ApiErrorCodeName = (typeof ApiErrorCode)[keyof typeof ApiErrorCode];

export type ApiErrorEnvelope = {
  status: typeof API_ERROR_STATUS;
  code: string;
  message: string;
  details?: Record<string, unknown>;
};

export type GraphqlErrorItem = {
  message: string;
  extensions?: Record<string, unknown>;
};

/** Duck-typed GraphQL / API failure (avoids circular import with GraphqlError). */
type ErrorLike = {
  errors?: readonly GraphqlErrorItem[];
  status?: number;
  message?: string;
  name?: string;
  code?: string | null;
  details?: Record<string, unknown> | null;
};

export function extensionCode(extensions?: Record<string, unknown> | null): string | null {
  const code = extensions?.code;
  return typeof code === "string" && code ? code : null;
}

export function extensionStatus(extensions?: Record<string, unknown> | null): number | null {
  const status = extensions?.status;
  return typeof status === "number" && Number.isFinite(status) ? status : null;
}

export function extensionDetails(
  extensions?: Record<string, unknown> | null,
): Record<string, unknown> | null {
  const details = extensions?.details;
  if (details && typeof details === "object" && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  return null;
}

/** Prefer GraphQL ``extensions.status``, then HTTP response status. */
export function resolveGraphqlStatus(
  errors: readonly GraphqlErrorItem[],
  httpStatus?: number,
): number | undefined {
  for (const err of errors) {
    const fromExt = extensionStatus(err.extensions);
    if (fromExt != null) return fromExt;
  }
  return httpStatus;
}

export function isApiErrorEnvelope(payload: unknown): payload is ApiErrorEnvelope {
  if (!payload || typeof payload !== "object") return false;
  const body = payload as Record<string, unknown>;
  return (
    body.status === API_ERROR_STATUS &&
    typeof body.code === "string" &&
    typeof body.message === "string"
  );
}

/**
 * Parse REST error JSON (new envelope or legacy ``{ detail }`` / FastAPI validation).
 */
export function parseApiErrorPayload(
  payload: unknown,
  fallbackMessage = "Request failed",
): { code: string | null; message: string; details: Record<string, unknown> | null } {
  if (isApiErrorEnvelope(payload)) {
    return {
      code: payload.code,
      message: payload.message || fallbackMessage,
      details: payload.details ?? null,
    };
  }

  if (payload && typeof payload === "object") {
    const body = payload as Record<string, unknown>;
    if (typeof body.message === "string" && body.message.trim()) {
      return {
        code: typeof body.code === "string" ? body.code : null,
        message: body.message,
        details:
          body.details && typeof body.details === "object" && !Array.isArray(body.details)
            ? (body.details as Record<string, unknown>)
            : null,
      };
    }
    if ("detail" in body) {
      const detail = body.detail;
      if (typeof detail === "string" && detail.trim()) {
        return { code: null, message: detail, details: null };
      }
      if (Array.isArray(detail)) {
        return {
          code: ApiErrorCode.ValidationError,
          message: detail.map(String).join("; "),
          details: { errors: detail },
        };
      }
    }
  }

  if (typeof payload === "string" && payload.trim()) {
    return { code: null, message: payload, details: null };
  }

  return { code: null, message: fallbackMessage, details: null };
}

function asErrorLike(err: unknown): ErrorLike | null {
  if (!err || typeof err !== "object") return null;
  return err as ErrorLike;
}

function isGraphqlErrorLike(err: unknown): err is ErrorLike & { errors: readonly GraphqlErrorItem[] } {
  const like = asErrorLike(err);
  return Boolean(like && like.name === "GraphqlError" && Array.isArray(like.errors));
}

export function graphqlErrorCode(err: unknown): string | null {
  if (!isGraphqlErrorLike(err)) return null;
  return extensionCode(err.errors[0]?.extensions);
}

export function graphqlErrorStatus(err: unknown): number | null {
  if (!isGraphqlErrorLike(err)) return null;
  if (typeof err.status === "number") return err.status;
  return extensionStatus(err.errors[0]?.extensions);
}

export function graphqlErrorDetails(err: unknown): Record<string, unknown> | null {
  if (!isGraphqlErrorLike(err)) return null;
  return extensionDetails(err.errors[0]?.extensions);
}

/** Field-level messages from BE ``ValidationError(details={field: msg})`` or FastAPI ``details.errors``. */
export function validationFieldMessages(
  details: Record<string, unknown> | null | undefined,
): Record<string, string> {
  if (!details) return {};
  const out: Record<string, string> = {};

  const nested = details.errors;
  if (Array.isArray(nested)) {
    for (const item of nested) {
      if (!item || typeof item !== "object") continue;
      const row = item as Record<string, unknown>;
      const loc = Array.isArray(row.loc) ? row.loc.filter((p) => p !== "body").join(".") : "";
      const msg = typeof row.msg === "string" ? row.msg : typeof row.message === "string" ? row.message : "";
      if (loc && msg) out[loc] = msg;
    }
  }

  for (const [key, value] of Object.entries(details)) {
    if (key === "errors") continue;
    if (typeof value === "string" && value.trim()) out[key] = value.trim();
  }
  return out;
}

export function formatDetailsMessage(details: Record<string, unknown> | null | undefined): string | null {
  const fields = validationFieldMessages(details);
  const parts = Object.entries(fields).map(([key, msg]) =>
    /^[a-zA-Z0-9_.]+$/.test(key) ? `${key}: ${msg}` : msg,
  );
  return parts.length ? parts.join("; ") : null;
}

export type DescribedApiError = {
  code: string | null;
  status: number | null;
  message: string;
  details: Record<string, unknown> | null;
  fields: Record<string, string>;
};

export function describeApiError(err: unknown, fallback = "Request failed"): DescribedApiError {
  if (isGraphqlErrorLike(err)) {
    const details = graphqlErrorDetails(err);
    const fields = validationFieldMessages(details);
    const raw = err.errors[0]?.message ?? err.message ?? fallback;
    const fromFields = formatDetailsMessage(details);
    const message =
      fromFields && (!raw || /^validation failed$/i.test(raw.trim())) ? fromFields : raw;
    return {
      code: graphqlErrorCode(err),
      status: graphqlErrorStatus(err),
      message: sanitizeUserFacingMessage(message || fallback),
      details,
      fields,
    };
  }

  const like = asErrorLike(err);
  if (err instanceof Error && err.message) {
    const code = typeof like?.code === "string" && like.code ? like.code : null;
    const status = typeof like?.status === "number" ? like.status : null;
    const details =
      like?.details && typeof like.details === "object" && !Array.isArray(like.details)
        ? like.details
        : null;
    const fields = validationFieldMessages(details);
    return {
      code,
      status,
      message: sanitizeUserFacingMessage(err.message),
      details,
      fields,
    };
  }

  return { code: null, status: null, message: fallback, details: null, fields: {} };
}

/** Stable ApplicationError code from GraphQL extensions or REST ``ImportApiError``. */
export function apiErrorCode(err: unknown): string | null {
  const fromGql = graphqlErrorCode(err);
  if (fromGql) return fromGql;
  const like = asErrorLike(err);
  return typeof like?.code === "string" && like.code ? like.code : null;
}

export function apiErrorStatus(err: unknown): number | null {
  const fromGql = graphqlErrorStatus(err);
  if (fromGql != null) return fromGql;
  const like = asErrorLike(err);
  return typeof like?.status === "number" ? like.status : null;
}

/** Strip env-var names and raw JSON payloads that must never reach end users. */
export function sanitizeUserFacingMessage(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return message;
  if (/Google OAuth client is not configured|Google OAuth is not configured|Google sign-in is not configured/i.test(trimmed)) {
    return m.auth_google_not_configured();
  }
  if (/Meta OAuth is not configured|Meta sign-in is not configured/i.test(trimmed)) {
    return m.auth_meta_not_configured();
  }
  if (/Failed to fetch|NetworkError|net::ERR_|Load failed|fetch failed/i.test(trimmed)) {
    return m.api_error_network();
  }
  // Env / secret names anywhere in the string (not only at the start).
  if (/\b[A-Z][A-Z0-9_]*(?:_TOKEN|_SECRET|_KEY|_PASSWORD|_CLIENT_ID|_CLIENT_SECRET)\b/.test(trimmed)) {
    return m.error_integration_not_configured();
  }
  if (/\.env\b|PUBLIC_[A-Z0-9_]+|authorizationUrl|redirect_uri/i.test(trimmed) && /not configured|missing|stub/i.test(trimmed)) {
    return m.error_integration_not_configured();
  }
  if (
    (trimmed.startsWith("{") && trimmed.includes("}")) ||
    (trimmed.startsWith("[") && trimmed.includes("]"))
  ) {
    try {
      JSON.parse(trimmed);
      return m.error_generic_retry();
    } catch {
      /* not pure JSON */
    }
  }
  if (/Twilio HTTP \d+:\s*[{\[]/.test(trimmed)) {
    return m.error_sms_delivery_failed();
  }
  return message;
}

export function errorMessage(err: unknown, fallback = "Request failed"): string {
  return describeApiError(err, fallback).message;
}

export function isUnauthorized(err: unknown): boolean {
  if (apiErrorStatus(err) === 401) return true;
  const code = apiErrorCode(err);
  return (
    code === ApiErrorCode.AuthenticationError ||
    code === "UNAUTHENTICATED" ||
    code === "AuthenticationError"
  );
}

export function isInsufficientCredits(err: unknown): boolean {
  if (apiErrorStatus(err) === 402) return true;
  return apiErrorCode(err) === ApiErrorCode.InsufficientCredits;
}

/**
 * BE raises ``AuthenticationError(code="totp_required")`` only when the user
 * has TOTP enrolled. Do not sniff message text — that false-positives on
 * unrelated copy (e.g. "authenticator") and shows a TOTP field for accounts
 * that never enrolled 2FA.
 */
export function isTotpRequired(err: unknown): boolean {
  return apiErrorCode(err) === "totp_required";
}

/** Soft-expired org — GraphQL write gate or REST import write gate. */
export function isSubscriptionExpired(err: unknown): boolean {
  return apiErrorCode(err) === ApiErrorCode.SubscriptionExpired;
}

export function isNotFound(err: unknown): boolean {
  if (apiErrorStatus(err) === 404) return true;
  return apiErrorCode(err) === ApiErrorCode.NotFoundError;
}

export function isForbidden(err: unknown): boolean {
  if (isSubscriptionExpired(err)) return false;
  if (apiErrorStatus(err) === 403) return true;
  return apiErrorCode(err) === ApiErrorCode.AuthorizationError;
}

export function isConflict(err: unknown): boolean {
  if (apiErrorStatus(err) === 409) return true;
  const code = apiErrorCode(err);
  return (
    code === ApiErrorCode.ConflictError ||
    code === ApiErrorCode.OptimisticLockError ||
    code === ApiErrorCode.WorkflowLocked
  );
}

export function isOptimisticLock(err: unknown): boolean {
  return apiErrorCode(err) === ApiErrorCode.OptimisticLockError;
}

export function isWorkflowLocked(err: unknown): boolean {
  return apiErrorCode(err) === ApiErrorCode.WorkflowLocked;
}

export function isExternalService(err: unknown): boolean {
  if (apiErrorStatus(err) === 502) return true;
  return apiErrorCode(err) === ApiErrorCode.ExternalServiceError;
}

export function isValidationError(err: unknown): boolean {
  const status = apiErrorStatus(err);
  const code = apiErrorCode(err);
  if (code === ApiErrorCode.ValidationError) return true;
  if (status === 400 || status === 422) {
    return !code || code === "bad_input";
  }
  return false;
}
